import { useState, useMemo } from 'react';
import { Drawer, Title, Text, Table, Badge, Group, Button, Stack, ActionIcon } from '@mantine/core';
import { ArrowUpDown, X } from 'lucide-react';
import type { DataLibrarySurveyRow } from '../../types';
import { SATISFACTION_COLORS } from './useCoachDashboardData';

interface CoachDashboardDrawerProps {
  opened: boolean;
  onClose: () => void;
  chartTitle: string;
  segmentLabel: string;
  rows: DataLibrarySurveyRow[];
  valueField: 'overall_satisfaction' | 'coach_satisfaction';
}

type SortKey = 'coach' | 'satisfaction' | 'group' | 'stage';
type SortOrder = 'asc' | 'desc';

function SatisfactionBadge({ value }: { value: string | null }) {
  if (!value) return <Text size="xs" c="dimmed">—</Text>;
  const color = SATISFACTION_COLORS[value];
  return (
    <Badge
      size="sm"
      styles={color ? {
        root: { backgroundColor: color },
        label: { color: '#fff', fontSize: 11, letterSpacing: 0.3, textTransform: 'none' },
      } : {
        label: { fontSize: 11, textTransform: 'none' },
      }}
      variant={color ? 'filled' : 'light'}
      color={color ? undefined : 'gray'}
    >
      {value}
    </Badge>
  );
}

function StageBadge({ stage }: { stage: string }) {
  return (
    <Badge size="sm" variant="light" color={stage === 'Midpoint' ? 'blue' : 'green'}>
      {stage}
    </Badge>
  );
}

export function CoachDashboardDrawer({
  opened,
  onClose,
  chartTitle,
  segmentLabel,
  rows,
  valueField,
}: CoachDashboardDrawerProps) {
  const [sortKey, setSortKey] = useState<SortKey>('satisfaction');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'coach':
          cmp = (a.coach_email || '').localeCompare(b.coach_email || '');
          break;
        case 'satisfaction':
          cmp = (a[valueField] || '').localeCompare(b[valueField] || '');
          break;
        case 'group':
          cmp = (a.group_name || '').localeCompare(b.group_name || '');
          break;
        case 'stage':
          cmp = (a.survey_stage || '').localeCompare(b.survey_stage || '');
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, sortKey, sortOrder, valueField]);

  const satisfactionLabel = valueField === 'overall_satisfaction' ? 'Overall satisfaction' : 'Coach satisfaction';

  const SortableHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(field)}
    >
      <Group gap={4} wrap="nowrap">
        {label}
        <ArrowUpDown size={12} opacity={sortKey === field ? 1 : 0.3} />
      </Group>
    </Table.Th>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Stack gap={4}>
          <Title order={4}>{chartTitle}</Title>
        </Stack>
      }
      padding="md"
    >
      <Stack gap="md">
        <Group gap="sm">
          <Badge variant="light" size="lg" color="gray">
            {segmentLabel}
          </Badge>
          <Button variant="subtle" size="xs" color="gray" onClick={onClose}>
            Reset
          </Button>
          <div style={{ flex: 1 }} />
          <ActionIcon variant="subtle" color="gray" onClick={onClose}>
            <X size={16} />
          </ActionIcon>
        </Group>

        <Text size="sm" c="dimmed">{sortedRows.length} records</Text>

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableHeader label="Coach" field="coach" />
              <SortableHeader label={satisfactionLabel} field="satisfaction" />
              <SortableHeader label="Group (Cohort)" field="group" />
              <SortableHeader label="Stage" field="stage" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedRows.map(row => (
              <Table.Tr key={row.submission_id}>
                <Table.Td>
                  <Text size="sm">{row.coach_email || row.coach_name || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <SatisfactionBadge value={row[valueField]} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.group_name || '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <StageBadge stage={row.survey_stage} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Drawer>
  );
}
